// Superconducting contacts in NEGF Transport (phase SC1): the "Contacts & fields" settings, the spec part they make
// (prayog_negf.sc_jobs on the server), the sweeps of a conductance map and the figures.
// Angles follow the measurements: φ is the polar angle from Z (the wire), θ the azimuth from X towards Y.

export interface ContactForm {
  name: string;
  delta_meV: number;              // gap at T = 0, B = 0
  phase_deg: number;
  Tc_K: number | null;            // null: no temperature dependence
  Bc_T: number[] | null;          // [Bc_x, Bc_y, Bc_z]; null: no field dependence
  mu_meV: number;                 // chemical-potential shift of the region or lead
  law?: GapLaw;                   // field law of the gap (SC4; absent in older settings: "gl")
  field_axes?: FieldAxes;         // how the field enters r (SC4; absent: "wire")
  axes_deg?: ContactAxes;         // field_axes "rotated": the axes of Bc_T
  response_T2?: number[][];       // field_axes "matrix": M (T⁻²), r = Bᵀ M B
}
/** r = Bᵀ M B (B in the wire's frame): "wire" M = diag(1/Bc²); "rotated" M = R diag(1/Bc²) Rᵀ, R from tilt, azimuth, roll;
 *  "matrix" M given directly (symmetric, positive semi-definite, T⁻²). */
export type FieldAxes = "wire" | "rotated" | "matrix";
export interface ContactAxes { tilt_deg: number; azimuth_deg: number; roll_deg: number }
export const FIELD_AXES: { value: FieldAxes; label: string }[] = [
  { value: "wire", label: "Bc along x, y, z" }, { value: "rotated", label: "Bc along rotated axes" }, { value: "matrix", label: "3×3 response M" }];
/** Δ(B) = Δ0·f(r), r = Σ (B_a/Bc_a)²: "gl" f = √(1 − r) (order parameter), "pair_breaking" f = 1 − r (spectral gap of a
 *  thin film with orbital pair breaking: it closes gradually). */
export type GapLaw = "gl" | "pair_breaking";
export const GAP_LAWS: { value: GapLaw; label: string }[] = [
  { value: "gl", label: "√(1 − r)" }, { value: "pair_breaking", label: "1 − r (pair breaking)" }];
/** The wire's axis in the magnet's frame (SC4): tilt from Z, azimuth from X towards Y. */
export interface Orientation { tilt_deg: number; azimuth_deg: number }

export type LeadKind = "normal" | number;          // a number: the index of a contact (a superconducting lead)
export type GMode = "isotropic" | "diagonal" | "tensor" | "element";

export interface RegionForm { cells: [number, number]; contact: number }   // [first, last + 1) along transport

export type SweepVariable = "B" | "angle" | "gate" | "mu";
export type Plane = "XY" | "XZ" | "YZ";
export interface SweepForm {
  variable: SweepVariable;
  min: number;
  max: number;
  n: number;
  direction: "x" | "y" | "z" | "angles";
  theta_deg: number;
  phi_deg: number;
  plane: Plane;
  B_T: number;
}

export type ScMethod = "coherent" | "tunnel";
/** Tier 2: a BCS-Dynes contact tunnelling into the wire (prayog_negf.sc_jobs "tunnel"). */
export interface TunnelForm {
  probe_contact: number;          // the tunnelling superconductor
  gamma_meV: number;              // its Dynes broadening
  G_N: number;                    // normal-state tunnel conductance (2e²/h)
  wire: "bdg" | "bcs";            // the wire's DOS: from the BdG model, or a second BCS superconductor
  wire_contact: number;           // "bcs": that superconductor
  wire_gamma_meV: number;
  cells: [number, number];        // "bdg": where the probe tunnels in
  dE_meV: number;                 // energy step of the wire's DOS
}
/** Coulomb diamonds (rate equations, prayog_negf.coulomb): levels from the model or entered, gate lever arm, rates. */
export interface CbForm {
  levels: "model" | "manual";
  manual_meV: number[];
  n_levels: number;
  lever_arm: number;
  n0: number;
  gate_mV: { min: number; max: number; n: number };
  gamma_L_ueV: number;
  gamma_R_ueV: number;
  rates: "constant" | "wavefunction";
  dynes_meV: number;
  // SC4: a dense-level (metallic) island with junction conductances (2e²/h), and maps over the field at a fixed gate
  island?: "levels" | "dense";
  G_L?: number;
  G_R?: number;
  mode?: "gate" | "field";
  gate_fixed_mV?: number;
  // SC5: bias split (μ_L = η V, μ_R = −(1 − η) V) and a field-dependent gate charge n_g(B) = n_g + BᵀQB (Q in e / T², or null)
  eta?: number;
  gate_Q_per_T2?: number[][] | null;
}
export function defaultCb(): CbForm {
  return { levels: "model", manual_meV: [0, 0, 0.5, 0.5], n_levels: 6, lever_arm: 0.1, n0: 0, gate_mV: { min: -20, max: 20, n: 81 },
    gamma_L_ueV: 1, gamma_R_ueV: 1, rates: "constant", dynes_meV: 0.01, island: "levels", G_L: 0.3, G_R: 0.3, mode: "gate", gate_fixed_mV: 0,
    eta: 0.5, gate_Q_per_T2: null };
}
export interface AbsForm { contact: number; phase_min_deg: number; phase_max_deg: number; n: number; levels: number; map: boolean }

/** What the "Contacts & fields" step holds (project.settings.negf.sc). */
export interface ScForm {
  contacts: ContactForm[];
  left: LeadKind;
  right: LeadKind;
  regions: RegionForm[];
  probe: 0 | 1 | null;
  g_mode: GMode;
  g: number;                       // isotropic
  g_diag: number[];                // diagonal gxx, gyy, gzz
  g_tensor: number[][];            // full 3x3
  g_element: Record<string, number[]>;   // per element: gxx, gyy, gzz
  B_T: number[];
  peierls: boolean;
  mu_meV: number;
  gate: { cells: [number, number] | null; V_meV: number };
  temperature_K: number;
  charging_energy_meV: number;
  bias: { min_mV: number; max_mV: number; n: number };
  sweep: SweepForm;
  gap: boolean;
  method?: ScMethod;
  tunnel?: TunnelForm;
  abs?: AbsForm;
  ldos?: { energy_meV: number };
  cb?: CbForm;
  orientation?: Orientation;
}

export function defaultTunnel(): TunnelForm {
  return { probe_contact: 0, gamma_meV: 0.005, G_N: 0.2, wire: "bdg", wire_contact: 0, wire_gamma_meV: 0, cells: [0, 1], dE_meV: 0.005 };
}
export function defaultAbs(): AbsForm {
  return { contact: 1, phase_min_deg: 0, phase_max_deg: 360, n: 37, levels: 12, map: false };
}

export const G_ELECTRON = 2.0023193;

export function defaultContact(name = "Pd"): ContactForm {
  return { name, delta_meV: 0.3, phase_deg: 0, Tc_K: null, Bc_T: null, mu_meV: 0, law: "gl" };
}

export function defaultSweep(): SweepForm {
  return { variable: "B", min: 0, max: 2, n: 21, direction: "z", theta_deg: 0, phi_deg: 0, plane: "XY", B_T: 0.9 };
}

export function defaultSc(species: string[] = []): ScForm {
  return {
    contacts: [defaultContact()], left: "normal", right: 0, regions: [], probe: null,
    g_mode: "isotropic", g: G_ELECTRON, g_diag: [G_ELECTRON, G_ELECTRON, G_ELECTRON],
    g_tensor: [[G_ELECTRON, 0, 0], [0, G_ELECTRON, 0], [0, 0, G_ELECTRON]],
    g_element: Object.fromEntries([...new Set(species)].map((el) => [el, [G_ELECTRON, G_ELECTRON, G_ELECTRON]])),
    B_T: [0, 0, 0], peierls: true, mu_meV: 0, gate: { cells: null, V_meV: 0 }, temperature_K: 0, charging_energy_meV: 0,
    bias: { min_mV: -1, max_mV: 1, n: 201 }, sweep: defaultSweep(), gap: false,
  };
}

/** B from |B| and the measurements' angles: (sin φ cos θ, sin φ sin θ, cos φ). */
type M3 = number[][];
const matmul = (A: M3, B: M3): M3 => A.map((r) => [0, 1, 2].map((j) => r[0] * B[0][j] + r[1] * B[1][j] + r[2] * B[2][j]));
const transpose = (A: M3): M3 => [0, 1, 2].map((i) => [0, 1, 2].map((j) => A[j][i]));

/** Columns: axes x″, y″, z″ — z″ at (tilt from z, azimuth from x towards y) by the smallest rotation, then a roll about
 *  z″ (as prayog_negf.sc.axes_rotation). */
export function axesRotation(tiltDeg: number, azimuthDeg: number, rollDeg = 0): M3 {
  const n = bSphericalRaw(1, azimuthDeg, tiltDeg);
  const ax = [-n[1], n[0], 0], s = Math.hypot(ax[0], ax[1]), c = n[2];
  let W: M3;
  if (s < 1e-12) W = c > 0 ? [[1, 0, 0], [0, 1, 0], [0, 0, 1]] : [[1, 0, 0], [0, -1, 0], [0, 0, -1]];
  else {
    const k = ax.map((v) => v / s);
    const K: M3 = [[0, -k[2], k[1]], [k[2], 0, -k[0]], [-k[1], k[0], 0]], KK = matmul(K, K);
    W = [0, 1, 2].map((i) => [0, 1, 2].map((j) => (i === j ? 1 : 0) + s * K[i][j] + (1 - c) * KK[i][j]));
  }
  const r = (rollDeg * Math.PI) / 180;
  return matmul(W, [[Math.cos(r), -Math.sin(r), 0], [Math.sin(r), Math.cos(r), 0], [0, 0, 1]]);
}

function bSphericalRaw(mag: number, thetaDeg: number, phiDeg: number): number[] {
  const th = (thetaDeg * Math.PI) / 180, ph = (phiDeg * Math.PI) / 180;
  return [mag * Math.sin(ph) * Math.cos(th), mag * Math.sin(ph) * Math.sin(th), mag * Math.cos(ph)];
}

/** A contact's field-response matrix M (T⁻²), or null for no field dependence. */
export function contactMatrix(c: ContactForm): M3 | null {
  const mode = c.field_axes ?? "wire";
  if (mode === "matrix") return c.response_T2 ?? null;
  if (!c.Bc_T) return null;
  const D: M3 = [0, 1, 2].map((i) => [0, 1, 2].map((j) => (i === j ? 1 / c.Bc_T![i] ** 2 : 0)));
  if (mode === "wire") return D;
  const a = c.axes_deg ?? { tilt_deg: 0, azimuth_deg: 0, roll_deg: 0 };
  const R = axesRotation(a.tilt_deg, a.azimuth_deg, a.roll_deg);
  return matmul(matmul(R, D), transpose(R));
}

/** Eigenvalues (ascending) and eigenvectors (columns) of a symmetric 3×3 matrix (Jacobi). */
export function eigSym3(A0: M3): { w: number[]; V: M3 } {
  const A = A0.map((r) => [...r]);
  let V: M3 = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  for (let sweep = 0; sweep < 50; sweep++) {
    const off = Math.abs(A[0][1]) + Math.abs(A[0][2]) + Math.abs(A[1][2]);
    if (off < 1e-15) break;
    for (const [p, q] of [[0, 1], [0, 2], [1, 2]]) {
      if (Math.abs(A[p][q]) < 1e-18) continue;
      const th = 0.5 * Math.atan2(2 * A[p][q], A[q][q] - A[p][p]), c = Math.cos(th), s = Math.sin(th);
      const J: M3 = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
      J[p][p] = c; J[q][q] = c; J[p][q] = s; J[q][p] = -s;
      const B = matmul(matmul(transpose(J), A), J);
      for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) A[i][j] = B[i][j];
      V = matmul(V, J);
    }
  }
  const order = [0, 1, 2].sort((i, j) => A[i][i] - A[j][j]);
  return { w: order.map((i) => A[i][i]), V: [0, 1, 2].map((r) => order.map((i) => V[r][i])) };
}

/** Principal critical fields (T, largest last: the most robust axis) and that axis' tilt from z and azimuth. */
export function responsePrincipal(M: M3): { Bc_T: number[]; tilt_deg: number; azimuth_deg: number } {
  const { w, V } = eigSym3(M);
  const z = [V[0][0], V[1][0], V[2][0]].map((v) => (V[2][0] >= 0 ? v : -v));
  const bc = (l: number) => (l > 1e-15 ? 1 / Math.sqrt(l) : Infinity);
  return { Bc_T: [bc(w[2]), bc(w[1]), bc(w[0])], tilt_deg: (Math.acos(Math.max(-1, Math.min(1, z[2]))) * 180) / Math.PI,
    azimuth_deg: Math.hypot(z[0], z[1]) > 1e-12 ? (Math.atan2(z[1], z[0]) * 180) / Math.PI : 0 };
}

/** The spec keys of a contact's field response. */
export function contactResponse(c: ContactForm): { axes_deg: ContactAxes | null; response_T2: number[][] | null } {
  const mode = c.field_axes ?? "wire";
  return { axes_deg: mode === "rotated" ? c.axes_deg ?? { tilt_deg: 0, azimuth_deg: 0, roll_deg: 0 } : null,
    response_T2: mode === "matrix" ? c.response_T2 ?? null : null };
}

export function bSpherical(mag: number, thetaDeg: number, phiDeg: number): number[] {
  const th = (thetaDeg * Math.PI) / 180, ph = (phiDeg * Math.PI) / 180;
  const r = (v: number) => (Math.abs(v) < 1e-12 ? 0 : +v.toPrecision(12));
  return [r(mag * Math.sin(ph) * Math.cos(th)), r(mag * Math.sin(ph) * Math.sin(th)), r(mag * Math.cos(ph))];
}

/** |B|, θ, φ of a field vector (θ = 0 for a field along Z). */
export function toSpherical(B: number[]): { mag: number; theta: number; phi: number } {
  const mag = Math.hypot(B[0], B[1], B[2]);
  if (mag === 0) return { mag: 0, theta: 0, phi: 0 };
  const phi = (Math.acos(Math.max(-1, Math.min(1, B[2] / mag))) * 180) / Math.PI;
  const theta = Math.hypot(B[0], B[1]) < 1e-12 ? 0 : (Math.atan2(B[1], B[0]) * 180) / Math.PI;
  return { mag, theta: +theta.toFixed(6), phi: +phi.toFixed(6) };
}

export const PLANES: { value: Plane; label: string; swept: "θ" | "φ"; about: string }[] = [
  { value: "XY", label: "XY (φ = 90°, θ from X to Y)", swept: "θ", about: "θ = 0: along X, 90°: along Y" },
  { value: "XZ", label: "XZ (θ = 0°, φ from Z to X)", swept: "φ", about: "φ = 0: along Z (the wire), 90°: along X" },
  { value: "YZ", label: "YZ (θ = 90°, φ from Z to Y)", swept: "φ", about: "φ = 0: along Z (the wire), 90°: along Y" },
];

export const SWEEP_VARIABLES: { value: SweepVariable; label: string; unit: string }[] = [
  { value: "B", label: "|B| along a direction", unit: "T" },
  { value: "angle", label: "Field angle in a plane", unit: "°" },
  { value: "gate", label: "Gate potential", unit: "meV" },
  { value: "mu", label: "Chemical potential μ", unit: "meV" },
];

/** The g-tensor as the server takes it. */
export function gSpec(f: ScForm): number | number[][] | Record<string, number[][]> {
  const diag = (d: number[]) => [[d[0], 0, 0], [0, d[1], 0], [0, 0, d[2]]];
  if (f.g_mode === "diagonal") return diag(f.g_diag);
  if (f.g_mode === "tensor") return f.g_tensor;
  if (f.g_mode === "element") return Object.fromEntries(Object.entries(f.g_element).map(([el, d]) => [el, diag(d)]));
  return f.g;
}

/** spec.superconductivity of a job. */
export function scSpec(f: ScForm, calc: ScCalc): Record<string, unknown> {
  const out: Record<string, unknown> = {
    contacts: f.contacts.map((c) => ({ name: c.name, delta_meV: c.delta_meV, phase_deg: c.phase_deg, Tc_K: c.Tc_K, Bc_T: c.Bc_T, mu_meV: c.mu_meV,
      law: c.law ?? "gl", ...contactResponse(c) })),
    left: f.left, right: f.right, regions: f.regions, probe: f.probe, g: gSpec(f), orientation: f.orientation ?? { tilt_deg: 0, azimuth_deg: 0 },
    B_T: f.B_T, peierls: f.peierls,
    mu_meV: f.mu_meV, gate: f.gate, temperature_K: f.temperature_K, charging_energy_meV: f.charging_energy_meV,
    bias: f.bias, gap: f.gap, method: f.method ?? "coherent",
  };
  if ((f.method ?? "coherent") === "tunnel") out.tunnel = f.tunnel ?? defaultTunnel();
  if (calc === "sc_map" || (calc === "sc_abs" && (f.abs ?? defaultAbs()).map)) out.sweep = f.sweep;
  if (calc === "sc_abs") out.abs = f.abs ?? defaultAbs();
  if (calc === "sc_ldos") out.ldos = f.ldos ?? { energy_meV: 0 };
  if (calc === "cb_map") {
    out.cb = f.cb ?? defaultCb();
    if ((f.cb?.mode ?? "gate") === "field") out.sweep = f.sweep;
  }
  return out;
}

export type ScCalc = "sc_conductance" | "sc_map" | "sc_abs" | "sc_ldos" | "cb_map";

/** Problems of the settings before asking the server. */
export function scProblems(f: ScForm, length: number, calc?: string): string[] {
  const out: string[] = [];
  if (!f.contacts.length) out.push("Add a superconducting contact.");
  f.contacts.forEach((c, i) => {
    if (!(c.delta_meV >= 0)) out.push(`Contact ${i + 1}: the gap cannot be negative.`);
    if (c.Tc_K != null && !(c.Tc_K > 0)) out.push(`Contact ${i + 1}: Tc above 0 K.`);
    if (c.Bc_T && c.Bc_T.some((b) => !(b > 0))) out.push(`Contact ${i + 1}: critical fields above 0 T.`);
    const mode = c.field_axes ?? "wire";
    if (mode === "rotated") {
      if (!c.Bc_T) out.push(`Contact ${i + 1}: rotated critical-field axes need the critical fields Bc.`);
      const t = c.axes_deg?.tilt_deg ?? 0;
      if (!(t >= 0 && t <= 180)) out.push(`Contact ${i + 1}: the critical-field axes' tilt is 0 … 180° from z.`);
    }
    if (mode === "matrix") {
      const M = c.response_T2;
      if (!M || M.length !== 3 || M.some((r) => r.length !== 3 || r.some((v) => !Number.isFinite(v))))
        out.push(`Contact ${i + 1}: the field-response matrix is 3×3 numbers (T⁻²).`);
      else if ([[0, 1], [0, 2], [1, 2]].some(([a, b]) => Math.abs(M[a][b] - M[b][a]) > 1e-9 * Math.max(1, Math.abs(M[a][b]))))
        out.push(`Contact ${i + 1}: the field-response matrix must be symmetric.`);
      else if (eigSym3(M).w[0] < -1e-9 * Math.max(1, ...M.flat().map(Math.abs)))
        out.push(`Contact ${i + 1}: the field-response matrix must be positive semi-definite (no field direction strengthens the gap).`);
    }
  });
  const o = f.orientation;
  if (o && !(o.tilt_deg >= 0 && o.tilt_deg <= 180)) out.push("The wire's tilt is 0 … 180° from Z.");
  const method = f.method ?? "coherent";
  const transport = calc === "sc_conductance" || calc === "sc_map";
  if (f.left !== "normal" && f.right !== "normal" && method === "coherent" && transport)
    out.push("Both leads are superconducting: the coherent G(V) needs a normal probe lead. Use the tunnel method (tier 2) for an S–wire–S junction, or the Andreev levels.");
  if (method === "tunnel" && transport) {
    const t = f.tunnel ?? defaultTunnel();
    if (!(t.probe_contact >= 0 && t.probe_contact < f.contacts.length)) out.push("Tunnel: choose the tunnelling superconductor.");
    if (!(t.G_N > 0)) out.push("Tunnel: the normal-state conductance must be above 0.");
    if (!(t.gamma_meV >= 0 && t.wire_gamma_meV >= 0)) out.push("Tunnel: Dynes broadenings of 0 or more.");
    if (!(t.dE_meV > 0)) out.push("Tunnel: the energy step must be positive.");
    if (t.wire === "bdg" && !(t.cells[0] >= 0 && t.cells[1] > t.cells[0] && t.cells[1] <= length)) out.push(`Tunnel: cells within 0 … ${length}.`);
    if (t.wire === "bcs" && !(t.wire_contact >= 0 && t.wire_contact < f.contacts.length)) out.push("Tunnel: choose the second superconductor.");
  }
  if (calc === "sc_abs") {
    const a = f.abs ?? defaultAbs();
    if (new Set(f.regions.map((r) => r.contact)).size < 2) out.push("Andreev levels need two superconducting regions with different contacts (S–wire–S).");
    if (!f.regions.some((r) => r.contact === a.contact)) out.push("Andreev levels: no region uses the contact whose phase is advanced.");
    if (!(a.n >= 2)) out.push("At least 2 phases.");
  }
  if (calc === "cb_map") {
    const c = f.cb ?? defaultCb();
    const dense = (c.island ?? "levels") === "dense", field = (c.mode ?? "gate") === "field";
    if (!(f.charging_energy_meV > 0)) out.push("Coulomb diamonds need a charging energy above 0 (step 4).");
    if (!dense && c.levels === "manual" && !(c.manual_meV.length >= 1 && c.manual_meV.length <= 8)) out.push("Enter 1 … 8 levels.");
    if (!dense && c.levels === "model" && !(c.n_levels >= 1 && c.n_levels <= 8)) out.push("1 … 8 levels from the model.");
    if (dense && !((c.G_L ?? 0) > 0 && (c.G_R ?? 0) > 0)) out.push("The junction conductances must be positive.");
    if (!(c.lever_arm > 0)) out.push("The lever arm must be positive.");
    if (!((c.eta ?? 0.5) > 0 && (c.eta ?? 0.5) < 1)) out.push("The bias split η must be between 0 and 1 (0.5 = symmetric).");
    const Q = c.gate_Q_per_T2;
    if (Q && !(Q.length === 3 && Q.every((row) => row.length === 3 && row.every(Number.isFinite)))) out.push("The field-dependent gate charge Q is a 3 × 3 matrix of numbers.");
    else if (Q && [0, 1, 2].some((a) => [0, 1, 2].some((b) => Math.abs(Q[a][b] - Q[b][a]) > 1e-12))) out.push("The field-dependent gate charge Q must be symmetric.");
    if (!field && (!(c.gate_mV.max > c.gate_mV.min) || !(c.gate_mV.n >= 2))) out.push("The gate range needs max > min and 2 points or more.");
    if (field && f.sweep.variable !== "B" && f.sweep.variable !== "angle") out.push("A Coulomb map over the field sweeps |B| or the field angle.");
    if (field && (!(f.sweep.max !== f.sweep.min) || !(f.sweep.n >= 2))) out.push("The sweep needs two different ends and 2 points or more.");
    if (!dense && !(c.gamma_L_ueV > 0 && c.gamma_R_ueV > 0)) out.push("The tunnel rates must be positive.");
    if (!dense && c.rates === "wavefunction" && c.levels === "manual") out.push("Rates from the wavefunctions need the levels from the model.");
    if (!(f.bias.max_mV > f.bias.min_mV) || !(f.bias.n >= 2)) out.push("The bias range needs max > min and 2 points or more.");
  }
  if (calc !== "cb_map" && f.left === "normal" && f.right === "normal" && !f.regions.length && !(method === "tunnel" && (f.tunnel ?? defaultTunnel()).wire === "bcs"))
    out.push("Nothing is superconducting: make a lead superconducting or add a proximitized region.");
  const spans = [...f.regions].sort((a, b) => a.cells[0] - b.cells[0]);
  f.regions.forEach((r, i) => {
    if (!(r.cells[0] >= 0 && r.cells[1] > r.cells[0] && r.cells[1] <= length)) out.push(`Region ${i + 1}: cells within 0 … ${length}.`);
    if (!(r.contact >= 0 && r.contact < f.contacts.length)) out.push(`Region ${i + 1}: choose a contact.`);
  });
  if (spans.some((s, i) => i > 0 && spans[i - 1].cells[1] > s.cells[0])) out.push("The regions overlap.");
  if (f.gate.cells && !(f.gate.cells[0] >= 0 && f.gate.cells[1] > f.gate.cells[0] && f.gate.cells[1] <= length))
    out.push(`Gate: cells within 0 … ${length}.`);
  if (!(f.temperature_K >= 0)) out.push("The temperature cannot be negative.");
  if (!(f.charging_energy_meV >= 0)) out.push("The charging energy cannot be negative.");
  if (calc === "sc_conductance" || calc === "sc_map") {
    if (!(f.bias.max_mV > f.bias.min_mV)) out.push("The bias range needs max > min.");
    if (!(f.bias.n >= 2)) out.push("At least 2 bias points.");
  }
  if (calc === "sc_map" || (calc === "sc_abs" && (f.abs ?? defaultAbs()).map)) {
    if (f.sweep.max === f.sweep.min) out.push("The sweep needs max ≠ min.");
    if (!(f.sweep.n >= 2)) out.push("At least 2 sweep points.");
  }
  return out;
}

/** "|B| 0…2 T along z" etc. for the list of runs and the axis titles. */
export function sweepLabel(w: SweepForm, gateCells?: [number, number] | null): string {
  if (w.variable === "B") return `|B| (T) ${w.direction === "angles" ? `at θ = ${w.theta_deg}°, φ = ${w.phi_deg}°` : `along ${w.direction}`}`;
  if (w.variable === "angle") return `${PLANES.find((p) => p.value === w.plane)?.swept ?? "θ"} (°) in the ${w.plane} plane, |B| = ${w.B_T} T`;
  if (w.variable === "gate") return `gate (meV)${gateCells ? ` on cells ${gateCells[0]}…${gateCells[1] - 1}` : ""}`;
  return "μ (meV)";
}

export function describeSc(s: Record<string, any>): string {
  const p = s.superconductivity ?? {};
  const leads = `${p.left === "normal" || p.left == null ? "N" : "S"}–${p.regions?.length ? "S" : "wire"}–${p.right === "normal" || p.right == null ? "N" : "S"}`;
  const B = (p.B_T ?? [0, 0, 0]) as number[];
  const bText = B.some((x) => x) ? `, B (${B.join(", ")}) T` : "";
  const bias = `${p.bias?.min_mV}…${p.bias?.max_mV} mV (${p.bias?.n})`;
  if (s.calculation === "sc_abs") {
    const a = p.abs ?? {};
    return `${s.name ? `${s.name} · ` : ""}Andreev levels, φ ${a.phase_min_deg}…${a.phase_max_deg}° (${a.n})${a.map ? `, I_c vs ${p.sweep?.variable}` : ""}`;
  }
  if (s.calculation === "cb_map") {
    const c = p.cb ?? {};
    const what = c.island === "dense" ? "dense-level island" : c.levels === "manual" ? `${c.manual_meV?.length} levels entered` : `${c.n_levels} levels of the wire`;
    const extra = `${c.eta != null && c.eta !== 0.5 ? `, η ${c.eta}` : ""}${c.gate_Q_per_T2 ? ", n_g(B) = n_g + BᵀQB" : ""}`;
    if (c.mode === "field") return `${s.name ? `${s.name} · ` : ""}Coulomb map over ${p.sweep?.variable === "angle" ? `the angle (${p.sweep?.plane}, ${p.sweep?.B_T} T)` : `|B| along ${p.sweep?.direction}`} at V_g ${c.gate_fixed_mV} mV, E_C ${p.charging_energy_meV} meV, ${what}${extra}, ${p.sweep?.n} × ${p.bias?.n} points`;
    return `${s.name ? `${s.name} · ` : ""}Coulomb diamonds, E_C ${p.charging_energy_meV} meV, ${what}${extra}, V_g ${c.gate_mV?.min}…${c.gate_mV?.max} mV (${c.gate_mV?.n}) × ${p.bias?.n} biases`;
  }
  if (s.calculation === "sc_ldos") return `${s.name ? `${s.name} · ` : ""}BdG LDOS at ${p.ldos?.energy_meV ?? 0} meV ${leads}`;
  const tier = p.method === "tunnel" ? " (tunnel)" : "";
  if (s.calculation === "sc_map") {
    const w = p.sweep ?? {};
    return `${s.name ? `${s.name} · ` : ""}G(V, ${w.variable})${tier} ${leads}: ${sweepLabel(w, p.gate?.cells)} ${w.min}…${w.max} (${w.n}), ${bias}`;
  }
  return `${s.name ? `${s.name} · ` : ""}G(V)${tier} ${leads}${bText}, ${bias}`;
}

/** Presets of the step. */
export type ScPreset = "ns" | "nsn" | "wire" | "te" | "sns";
export function scPreset(kind: ScPreset, length: number, f: ScForm): ScForm {
  const c = f.contacts.length ? f.contacts : [defaultContact()];
  if (kind === "ns") return { ...f, contacts: c, left: "normal", right: 0, regions: [], probe: 0 };
  if (kind === "nsn") {
    const a = Math.max(1, Math.round(length / 3)), b = Math.max(a + 1, Math.round((2 * length) / 3));
    return { ...f, contacts: c, left: "normal", right: "normal", regions: [{ cells: [a, b], contact: 0 }], probe: 0 };
  }
  if (kind === "te") {
    // the Te / PdxTe measurements: two PdxTe contacts (Delta ~ 0.3 meV each: gap edge 0.6 mV), weak tunnelling,
    // critical fields that make B along the wire (z) close the gap at ~1.5x the field across it
    const pd = (name: string): ContactForm => ({ name, delta_meV: 0.3, phase_deg: 0, Tc_K: 4.5, Bc_T: [0.9, 0.9, 1.4], mu_meV: 0 });
    return { ...f, contacts: [pd("PdTe (probe)"), pd("PdTe")], left: 1, right: 1, regions: [{ cells: [0, length], contact: 1 }], probe: null,
      method: "tunnel", tunnel: { ...defaultTunnel(), probe_contact: 0, G_N: 0.25, gamma_meV: 0.01, wire: "bdg", cells: [0, Math.min(1, length)] },
      temperature_K: 0.05, bias: { min_mV: -1, max_mV: 1, n: 201 } };
  }
  if (kind === "sns") {
    const a = Math.max(1, Math.round(length / 2) - 1), b = Math.min(length - 1, a + 2);
    const c2 = c.length > 1 ? c : [...c, { ...c[0], name: `${c[0].name} 2` }];
    return { ...f, contacts: c2, left: "normal", right: "normal", regions: [{ cells: [0, a], contact: 0 }, { cells: [b, length], contact: 1 }],
      probe: null, abs: { ...defaultAbs(), contact: 1 } };
  }
  // a wire on a superconductor: everything proximitized after a tunnel barrier next to the probe
  return { ...f, contacts: c, left: "normal", right: 0, regions: [{ cells: [Math.min(2, length - 1), length], contact: 0 }], probe: 0,
    gate: { cells: [Math.min(1, length - 1), Math.min(2, length)], V_meV: f.gate.V_meV || 500 } };
}

// -- figures -------------------------------------------------------------------------------------------------------
/** The device along transport: leads, superconducting regions and the gate, cell by cell. */
export function scheme(f: ScForm, length: number) {
  const shapes: unknown[] = [];
  const notes: unknown[] = [];
  const lead = (x0: number, x1: number, kind: LeadKind, label: string) => {
    const sc = kind !== "normal";
    shapes.push({ type: "rect", x0, x1, y0: 0, y1: 1, fillcolor: sc ? "rgba(122,63,191,0.45)" : "rgba(235,104,52,0.35)", line: { width: 0 } });
    notes.push({ x: (x0 + x1) / 2, y: 0.5, showarrow: false, font: { size: 11 },
      text: `${label}<br>${sc ? `S: ${f.contacts[kind as number]?.name ?? "?"}` : "normal"}` });
  };
  lead(-3, 0, f.left, "left lead");
  lead(length, length + 3, f.right, "right lead");
  shapes.push({ type: "rect", x0: 0, x1: length, y0: 0, y1: 1, fillcolor: "rgba(42,120,214,0.15)", line: { color: "#2a78d6", width: 1 } });
  for (const r of f.regions) {
    shapes.push({ type: "rect", x0: r.cells[0], x1: r.cells[1], y0: 0.15, y1: 0.85, fillcolor: "rgba(122,63,191,0.55)", line: { width: 0 } });
    notes.push({ x: (r.cells[0] + r.cells[1]) / 2, y: 0.5, showarrow: false, font: { size: 10, color: "white" },
      text: f.contacts[r.contact]?.name ?? "S" });
  }
  const g = f.gate.cells ?? (f.gate.V_meV || f.sweep.variable === "gate" ? [0, length] : null);
  if (g) {
    shapes.push({ type: "rect", x0: g[0], x1: g[1], y0: 1.1, y1: 1.35, fillcolor: "rgba(237,161,0,0.75)", line: { color: "#b37800", width: 1 } });
    notes.push({ x: (g[0] + g[1]) / 2, y: 1.5, showarrow: false, font: { size: 10 }, text: `gate ${f.gate.V_meV} meV` });
  }
  for (let x = 0; x <= length; x++) shapes.push({ type: "line", x0: x, x1: x, y0: 0, y1: 1, line: { color: "rgba(0,0,0,0.12)", width: 1 } });
  return {
    data: [{ type: "scatter", x: [-3, length + 3], y: [0, 1.6], mode: "markers", marker: { opacity: 0 }, hoverinfo: "skip", showlegend: false }],
    layout: { title: { text: "Along the transport direction (cells)" }, shapes, annotations: notes, showlegend: false,
      xaxis: { title: { text: "cell" }, range: [-3.2, length + 3.2], zeroline: false },
      yaxis: { visible: false, range: [-0.1, 1.7] }, margin: { l: 20, r: 20, t: 40, b: 40 } },
  };
}

export function conductanceFigure(r: Record<string, any>) {
  const tunnel = r.method === "tunnel";
  const data: unknown[] = [
    { type: "scatter", mode: "lines", name: tunnel ? `tunnel dI/dV (T = ${r.temperature_K ?? 0} K)` : "T = 0", x: r.V, y: r.G,
      line: { color: "#2e213e", width: 2 } },
  ];
  if (!tunnel && r.temperature_K > 0)
    data.push({ type: "scatter", mode: "lines", name: `T = ${r.temperature_K} K`, x: r.V, y: r.G_T, line: { color: "#eb6834", width: 1.6 } });
  if (r.R_he) data.push({ type: "scatter", mode: "lines", name: "Andreev R_he / 2", x: r.V, y: (r.R_he as number[]).map((v) => v / 2),
    line: { color: "#7a3fbf", width: 1, dash: "dot" }, visible: "legendonly" });
  if (r.measured) data.push({ type: "scatter", mode: "lines", name: "measured", x: r.measured.V, y: r.measured.G,
    line: { color: "#1baf7a", width: 1.6, dash: "dash" } });
  const gaps = (r.contact_gaps_meV ?? []) as number[];
  const shapes = (tunnel ? [] : gaps).flatMap((d) => (d > 0 ? [-d, d] : [])).map((x) => ({ type: "line", x0: x, x1: x, yref: "paper", y0: 0, y1: 1,
    line: { color: "#7a3fbf", width: 1, dash: "dash" } }));
  return {
    data,
    layout: { title: { text: r.name || "Differential conductance" }, xaxis: { title: { text: "Bias V (mV)" } },
      yaxis: { title: { text: "dI/dV (2e²/h)" }, rangemode: "tozero" }, shapes, legend: { orientation: "h", y: -0.2 } },
  };
}

export function scMapFigure(r: Record<string, any>, thermal = true) {
  const z = thermal && r.temperature_K > 0 ? r.G_T : r.G;
  return {
    data: [{ type: "heatmap", x: r.V, y: r.X, z, colorscale: "Magma", colorbar: { title: { text: "2e²/h" } },
      hovertemplate: `V %{x:.4g} mV<br>${r.variable} %{y:.4g} ${r.unit}<br>G %{z:.4f}<extra></extra>` }],
    layout: { title: { text: r.name || "Conductance map" }, xaxis: { title: { text: "Bias V (mV)" } },
      yaxis: { title: { text: r.label ?? r.variable } } },
  };
}

export function scCutFigure(r: Record<string, any>, i: number) {
  return conductanceFigure({ ...r, name: `G(V) at ${r.variable} = ${(+r.X[i]).toPrecision(4)} ${r.unit}`,
    G: r.G[i], G_T: r.G_T[i], R_he: r.R_he?.[i], contact_gaps_meV: r.contact_gaps_meV?.[i] });
}

/** The proximitized wire's BdG gap and the contacts' Δ(B, T) against the sweep. */
export function gapFigure(r: Record<string, any>) {
  const data: unknown[] = [];
  if (r.wire_gap_meV) data.push({ type: "scatter", mode: "lines+markers", name: "wire BdG gap", x: r.X, y: r.wire_gap_meV, line: { color: "#2e213e" } });
  const n = r.contact_gaps_meV?.[0]?.length ?? 0;
  for (let k = 0; k < n; k++)
    data.push({ type: "scatter", mode: "lines", name: `Δ contact ${k + 1}`, x: r.X, y: (r.contact_gaps_meV as number[][]).map((g) => g[k]),
      line: { dash: "dash" } });
  return { data, layout: { title: { text: "Gaps along the sweep" }, xaxis: { title: { text: r.label ?? r.variable } },
    yaxis: { title: { text: "meV" }, rangemode: "tozero" }, legend: { orientation: "h", y: -0.2 } } };
}


export function absFigure(r: Record<string, any>) {
  const E = r.E_meV as number[][];
  const nl = E[0]?.length ?? 0;
  const data: unknown[] = [];
  for (let k = 0; k < nl; k++) {
    const y = E.map((row) => row[k]);
    data.push({ type: "scatter", mode: "lines", x: r.phase_deg, y, line: { color: "#2e213e", width: 1.4 }, showlegend: false, hoverinfo: "x+y" });
    data.push({ type: "scatter", mode: "lines", x: r.phase_deg, y: y.map((v) => -v), line: { color: "#2e213e", width: 1.4 }, showlegend: false, hoverinfo: "x+y" });
  }
  const gaps = (r.contact_gaps_meV ?? []) as number[];
  const shapes = gaps.filter((d) => d > 0).flatMap((d) => [d, -d]).map((y) => ({ type: "line", xref: "paper", x0: 0, x1: 1, y0: y, y1: y,
    line: { color: "#7a3fbf", width: 1, dash: "dash" } }));
  return { data, layout: { title: { text: `${r.name || "Andreev levels"}` }, xaxis: { title: { text: "phase difference φ (°)" }, dtick: 90 },
    yaxis: { title: { text: "E (meV)" } }, shapes } };
}

export function currentFigure(r: Record<string, any>) {
  return { data: [{ type: "scatter", mode: "lines+markers", x: r.phase_deg, y: r.I_nA, line: { color: "#eb6834", width: 2 }, marker: { size: 4 } }],
    layout: { title: { text: `Supercurrent I(φ) · I_c = ${(+r.Ic_nA).toPrecision(4)} nA` }, xaxis: { title: { text: "phase difference φ (°)" }, dtick: 90 },
      yaxis: { title: { text: "I (nA)" } } } };
}

export function icFigure(r: Record<string, any>) {
  return { data: [{ type: "scatter", mode: "lines+markers", x: r.X, y: r.Ic_nA, line: { color: "#2e213e", width: 2 }, marker: { size: 5 } }],
    layout: { title: { text: r.name || "Critical current" }, xaxis: { title: { text: r.label ?? r.variable } },
      yaxis: { title: { text: "I_c (nA)" }, rangemode: "tozero" } } };
}

export function wireDosFigure(r: Record<string, any>) {
  return { data: [{ type: "scatter", mode: "lines", x: r.dos_E, y: r.dos, line: { color: "#2a78d6", width: 1.6 } }],
    layout: { title: { text: "The wire's density of states where the probe tunnels in" }, xaxis: { title: { text: "E (meV)" } },
      yaxis: { title: { text: "n(E) / n_normal" }, rangemode: "tozero" } } };
}

/** G(V_g, V) of the rate equations, the experiment's way: gate across, bias up. */
export function cbMapFigure(r: Record<string, any>) {
  const G = r.G as number[][];
  const z = (r.V as number[]).map((_, j) => G.map((row) => row[j]));
  return {
    data: [{ type: "heatmap", x: r.Vg, y: r.V, z, colorscale: "Magma", colorbar: { title: { text: "2e²/h" } },
      hovertemplate: "V_g %{x:.4g} mV<br>V %{y:.4g} mV<br>G %{z:.3g}<extra></extra>" }],
    layout: { title: { text: r.name || "Coulomb diamonds" }, xaxis: { title: { text: "gate V_g (mV)" } }, yaxis: { title: { text: "bias V (mV)" } } },
  };
}

export function zeroBiasFigure(r: Record<string, any>) {
  const V = r.V as number[];
  let j = 0;
  V.forEach((v, k) => { if (Math.abs(v) < Math.abs(V[j])) j = k; });
  return {
    data: [
      { type: "scatter", mode: "lines", name: `G at V = ${V[j]} mV`, x: r.Vg, y: (r.G as number[][]).map((row) => row[j]), line: { color: "#2e213e", width: 2 } },
      { type: "scatter", mode: "lines", name: "electrons N (ground state)", x: r.Vg, y: r.N0, yaxis: "y2", line: { color: "#eb6834", shape: "hv", dash: "dot" } },
    ],
    layout: { title: { text: "Coulomb peaks at zero bias" }, xaxis: { title: { text: "gate V_g (mV)" } }, yaxis: { title: { text: "dI/dV (2e²/h)" } },
      yaxis2: { title: { text: "N" }, overlaying: "y", side: "right", showgrid: false, dtick: 1 }, legend: { orientation: "h", y: -0.2 } },
  };
}
